import { useState, useEffect } from 'react';
import { TextInput, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@c2fo/liquidity';

interface RRuleEditorProps {
  value: string;
  onChange: (expression: string) => void;
}

type Frequency = 'MINUTELY' | 'HOURLY' | 'DAILY' | 'WEEKLY' | 'MONTHLY';

const FREQUENCY_OPTIONS: { value: Frequency; label: string }[] = [
  { value: 'MINUTELY', label: 'Every X minutes' },
  { value: 'HOURLY', label: 'Every X hours' },
  { value: 'DAILY', label: 'Every X days' },
  { value: 'WEEKLY', label: 'Weekly' },
  { value: 'MONTHLY', label: 'Monthly' },
];

const DAYS_OF_WEEK = [
  { value: 'SU', label: 'Sun' },
  { value: 'MO', label: 'Mon' },
  { value: 'TU', label: 'Tue' },
  { value: 'WE', label: 'Wed' },
  { value: 'TH', label: 'Thu' },
  { value: 'FR', label: 'Fri' },
  { value: 'SA', label: 'Sat' },
];

const DAYS_OF_MONTH = Array.from({ length: 31 }, (_, i) => ({
  value: String(i + 1),
  label: String(i + 1),
}));

const parseRRule = (
  rrule: string,
): {
  frequency: Frequency;
  interval: string;
  byDay: string[];
  byMonthDay: string;
} => {
  const defaults = { frequency: 'DAILY' as Frequency, interval: '1', byDay: [] as string[], byMonthDay: '1' };
  if (!rrule) return defaults;

  const freqMatch = rrule.match(/FREQ=(\w+)/);
  const intervalMatch = rrule.match(/INTERVAL=(\d+)/);
  const byDayMatch = rrule.match(/BYDAY=([A-Z,]+)/);
  const byMonthDayMatch = rrule.match(/BYMONTHDAY=(\d+)/);

  return {
    frequency: (freqMatch?.[1] as Frequency) || 'DAILY',
    interval: intervalMatch?.[1] || '1',
    byDay: byDayMatch?.[1]?.split(',') ?? [],
    byMonthDay: byMonthDayMatch?.[1] || '1',
  };
};

export const RRuleEditor: React.FC<RRuleEditorProps> = ({ value, onChange }) => {
  const [isAdvanced, setIsAdvanced] = useState(false);
  const [frequency, setFrequency] = useState<Frequency>('DAILY');
  const [interval, setInterval] = useState('1');
  const [byDay, setByDay] = useState<string[]>([]);
  const [byMonthDay, setByMonthDay] = useState('1');

  useEffect(() => {
    if (value) {
      const parsed = parseRRule(value);
      setFrequency(parsed.frequency);
      setInterval(parsed.interval);
      setByDay(parsed.byDay);
      setByMonthDay(parsed.byMonthDay);
    }
  }, [value]);

  const buildRRule = (freq: Frequency, intv: string, days: string[], monthDay: string): string => {
    const parts = [`FREQ=${freq}`];

    if (intv && intv !== '1') {
      parts.push(`INTERVAL=${intv}`);
    }

    if (freq === 'WEEKLY' && days.length > 0) {
      parts.push(`BYDAY=${days.join(',')}`);
    }

    if (freq === 'MONTHLY') {
      parts.push(`BYMONTHDAY=${monthDay}`);
    }

    return parts.join(';');
  };

  const handleFrequencyChange = (newFreq: Frequency) => {
    setFrequency(newFreq);
    onChange(buildRRule(newFreq, interval, byDay, byMonthDay));
  };

  const handleIntervalChange = (newInterval: string) => {
    const sanitized = newInterval.replace(/\D/g, '') || '1';
    setInterval(sanitized);
    onChange(buildRRule(frequency, sanitized, byDay, byMonthDay));
  };

  const handleDayToggle = (day: string) => {
    const newDays = byDay.includes(day) ? byDay.filter((d) => d !== day) : [...byDay, day];
    setByDay(newDays);
    onChange(buildRRule(frequency, interval, newDays, byMonthDay));
  };

  const handleMonthDayChange = (day: string) => {
    setByMonthDay(day);
    onChange(buildRRule(frequency, interval, byDay, day));
  };

  if (isAdvanced) {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="block text-sm font-medium text-gray-700">RRULE Expression</Label>
          <button
            type="button"
            onClick={() => setIsAdvanced(false)}
            className="text-xs text-primary-600 hover:text-primary-700 hover:underline"
          >
            Use visual editor
          </button>
        </div>
        <TextInput
          value={value}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
          placeholder="FREQ=DAILY;INTERVAL=1"
          className="w-full font-mono"
          size="sm"
        />
        <p className="text-xs text-gray-500">RFC 5545 recurrence rule format</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="block text-sm font-medium text-gray-700">Recurrence Pattern</Label>
        <button
          type="button"
          onClick={() => setIsAdvanced(true)}
          className="text-xs text-primary-600 hover:text-primary-700 hover:underline"
        >
          Advanced
        </button>
      </div>

      <div>
        <Label className="block text-xs text-gray-500 mb-1">Frequency</Label>
        <Select value={frequency} onValueChange={(val) => handleFrequencyChange(val as Frequency)}>
          <SelectTrigger className="w-full py-2.5" size="sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {FREQUENCY_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {(frequency === 'MINUTELY' || frequency === 'HOURLY' || frequency === 'DAILY') && (
        <div>
          <Label className="block text-xs text-gray-500 mb-1">
            Every {frequency === 'MINUTELY' ? 'X minutes' : frequency === 'HOURLY' ? 'X hours' : 'X days'}
          </Label>
          <TextInput
            value={interval}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleIntervalChange(e.target.value)}
            placeholder="1"
            className="w-24"
            size="sm"
            type="number"
            min="1"
          />
        </div>
      )}

      {frequency === 'WEEKLY' && (
        <div>
          <Label className="block text-xs text-gray-500 mb-2">Days of Week</Label>
          <div className="flex gap-2">
            {DAYS_OF_WEEK.map((day) => (
              <button
                key={day.value}
                type="button"
                onClick={() => handleDayToggle(day.value)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                  byDay.includes(day.value)
                    ? 'bg-primary-600 text-white border-primary-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {day.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {frequency === 'MONTHLY' && (
        <div>
          <Label className="block text-xs text-gray-500 mb-1">Day of Month</Label>
          <Select value={byMonthDay} onValueChange={handleMonthDayChange}>
            <SelectTrigger className="w-24 py-2.5" size="sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DAYS_OF_MONTH.map((d) => (
                <SelectItem key={d.value} value={d.value}>
                  {d.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="pt-2 border-t border-gray-100">
        <p className="text-xs text-gray-500">
          RRULE: <code className="bg-gray-100 px-1 py-0.5 rounded font-mono">{value || '—'}</code>
        </p>
      </div>
    </div>
  );
};
